<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { PlusIcon } from '@heroicons/vue/20/solid'
import BigNumber from 'bignumber.js'
import sortBy from 'lodash/sortBy'
import type { BpProduct } from '~/definitions/AllDefinitions'
import { useMainStore } from '~/store/MainStore'
import { canOrder } from '~/classes/HourUtils'

const props = defineProps<{
  product: BpProduct
  single: boolean
}>()
const { t } = useI18n()
const emit = defineEmits(['view-product'])
const { currencyCode, isOrderingEnabled, timeZone } = storeToRefs(useMainStore())
const thumbnail = computed(() => {
  const defImg =
    props.product.image && props.product.image.original
      ? props.product.image.original
      : 'default-product-thumbnail.svg'
  return props.product.image && props.product.image.sizes
    ? props.product.image.sizes.s280
      ? props.product.image.sizes.s280
      : props.product.image.sizes.s200
        ? props.product.image.sizes.s200
        : props.product.image.sizes.s64
          ? props.product.image.sizes.s64
          : defImg
    : defImg
})
const price = computed(() => {
  const defaultRet = {
    min: 0,
    max: 0,
    isRange: false,
    value: props.product && typeof props.product.price !== 'undefined' ? props.product.price : 0,
  }
  if (!props.product) {
    return defaultRet
  }
  defaultRet.min = props.product.price
  if (!props.product.productModifierGroups?.length) {
    return defaultRet
  } else {
    let min: number | BigNumber = new BigNumber(props.product.price)
    let max: number | BigNumber = new BigNumber(0)
    const optionModifiers = props.product.productModifierGroups.filter((mod) => {
      return mod.type === 'options'
    })
    if (optionModifiers) {
      let prices: number[] = optionModifiers
        .filter(group => group.modifiers?.length)
        .flatMap(mod => mod.modifiers)
        .map(mod => mod.price)
      prices = sortBy(prices)
      if (prices.length) {
        min = min.plus(prices[0]).toNumber()
        max = new BigNumber(props.product.price).plus(prices[prices.length - 1] as number).toNumber()
        if (min !== max) {
          return {
            min,
            max,
            isRange: true,
            value: props.product.price,
          }
        }
      }
    }
    return defaultRet
  }
})
const enableOrdering = computed(() => {
  return canOrder(props.product, Boolean(isOrderingEnabled.value), timeZone.value)
})
function viewProduct() {
  emit('view-product', props.product)
}
const altText = computed(() => {
  return props.product.image && props.product.image.altText
    ? props.product.image.altText
    : props.product.name
})
</script>

<template>
  <div
    class="group cursor-pointer w-full"
    @click="viewProduct()"
  >
    <div class="grid sm:grid-cols-1 grid-cols-12 gap-5">
      <div class="sm:col-auto col-span-3">
        <div class="group rounded-lg aspect-square bg-sky-50 group-hover:opacity-75 relative">
          <div
            v-if="product.outOfStock"
            class="out-of-stock"
          >
            {{ t('OUT_OF_STOCK') }}
          </div>
          <img
            :src="thumbnail"
            :alt="altText"
            class="h-full w-full object-cover object-center rounded-lg"
          >
          <div
            v-if="enableOrdering"
            class="add-to-cart sm:flex hidden"
          >
            <PlusIcon
              class="fill-accent h-5 w-5"
              aria-hidden="true"
            />
          </div>
          <div
            v-else-if="!product.outOfStock"
            class="view-product-details sm:block hidden"
          >
            {{ t('VIEW') }}
          </div>
        </div>
      </div>
      <div class="sm:col-auto col-span-9">
        <div class="flex w-auto items-center justify-between text-base font-medium text-slate-900 dark:text-slate-50">
          <h3 class="mr-2">
            {{ product.name }}
          </h3>
          <p>
            {{ $filters.formatCurrency(price.min, currencyCode) }}<span v-if="price.isRange">+</span>
          </p>
        </div>
        <div
          v-if="product.description"
          class="mt-1 text-sm text-slate-500 dark:text-slate-400 tw-truncate"
        >
          {{ product.description }}
        </div>
        <div class="tw-text-button text-sm mt-1">
          <button
            v-if="enableOrdering"
            type="button"
          >
            {{ t('ADD_TO_CART') }} &rsaquo;
          </button>
          <button
            v-else
            type="button"
          >
            {{ t('VIEW') }} &rsaquo;
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
